import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tag, Plus, Trash2, Power, Users, X, Package, Download, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  code: "",
  discount_percent: 20,
  bonus_tokens: 0,
  applicable_tiers: ["premium", "platinum"],
  max_uses: "",
  single_use_per_user: true,
  is_active: true,
  description: "",
};

const emptyBulkForm = {
  prefix: "MIA",
  batch_label: "",
  count: 50,
  discount_percent: 20,
  bonus_tokens: 0,
  applicable_tiers: ["premium", "platinum"],
  valid_days: 90,
  suffix_length: 4,
};

const AdminCouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [redemptionsOpen, setRedemptionsOpen] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  // Bulk batches state
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [bulkForm, setBulkForm] = useState(emptyBulkForm);
  const [creatingBulk, setCreatingBulk] = useState(false);
  const [batches, setBatches] = useState([]);
  const [lastBatchCodes, setLastBatchCodes] = useState(null);

  const fetchBatches = async () => {
    try {
      const r = await axios.get(`${API}/admin/coupon-batches`);
      setBatches(r.data.batches || []);
    } catch (e) {
      /* batches are non-critical */
    }
  };

  const fetchCoupons = async () => {
    try {
      const r = await axios.get(`${API}/admin/coupons`);
      setCoupons(r.data.coupons || []);
    } catch (e) {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/coupons`);
        if (!cancelled) setCoupons(r.data.coupons || []);
      } catch {
        if (!cancelled) toast.error("Failed to load coupons");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/coupon-batches`);
        if (!cancelled) setBatches(r.data.batches || []);
      } catch { /* batches non-critical */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCreate = async () => {
    if (!form.code.trim()) { toast.error("Code is required"); return; }
    if (!form.discount_percent && !form.bonus_tokens) {
      toast.error("Set either a discount % or bonus tokens"); return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/coupons`, {
        ...form,
        code: form.code.trim().toUpperCase(),
        discount_percent: Number(form.discount_percent) || 0,
        bonus_tokens: Number(form.bonus_tokens) || 0,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
      });
      toast.success(`Coupon ${form.code.toUpperCase()} created`);
      setForm(emptyForm); setShowForm(false); fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create coupon");
    } finally { setCreating(false); }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/coupons/${c.code}`, { is_active: !c.is_active });
      toast.success(`${c.code} ${c.is_active ? "deactivated" : "activated"}`);
      fetchCoupons();
    } catch (e) { toast.error("Update failed"); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete coupon ${code}? This is permanent.`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${code}`);
      toast.success(`${code} deleted`);
      fetchCoupons();
    } catch (e) { toast.error("Delete failed"); }
  };

  const viewRedemptions = async (code) => {
    setRedemptionsOpen(code);
    try {
      const r = await axios.get(`${API}/admin/coupons/${code}/redemptions`);
      setRedemptions(r.data.redemptions || []);
    } catch { setRedemptions([]); }
  };

  const handleCreateBatch = async () => {
    const prefix = (bulkForm.prefix || "").trim().toUpperCase();
    if (!prefix) { toast.error("Prefix is required"); return; }
    if (!bulkForm.batch_label.trim()) { toast.error("Batch label is required"); return; }
    if (!bulkForm.discount_percent && !bulkForm.bonus_tokens) {
      toast.error("Set either a discount % or bonus tokens"); return;
    }
    setCreatingBulk(true);
    try {
      const r = await axios.post(`${API}/admin/coupon-batches`, {
        ...bulkForm,
        prefix,
        count: Number(bulkForm.count) || 0,
        discount_percent: Number(bulkForm.discount_percent) || 0,
        bonus_tokens: Number(bulkForm.bonus_tokens) || 0,
        valid_days: Number(bulkForm.valid_days) || 90,
        suffix_length: Number(bulkForm.suffix_length) || 4,
      });
      toast.success(`Created ${r.data.codes.length} codes`);
      setLastBatchCodes({ batchId: r.data.batch.id, codes: r.data.codes });
      setBulkForm(emptyBulkForm);
      setShowBulkForm(false);
      fetchBatches();
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk generation failed");
    } finally { setCreatingBulk(false); }
  };

  const handleExportBatch = async (batchId, label) => {
    try {
      const r = await axios.get(`${API}/admin/coupon-batches/${batchId}/export`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (label || "batch").replace(/[^a-z0-9]/gi, "_").slice(0, 40);
      a.download = `xea-coupons-${safe}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const copyCodesToClipboard = (codes) => {
    navigator.clipboard.writeText(codes.join("\n")).then(
      () => toast.success(`Copied ${codes.length} codes`),
      () => toast.error("Clipboard blocked"),
    );
  };

  if (loading) return <div className="text-center text-ink-500 py-12">Loading coupons…</div>;

  return (
    <div className="space-y-6" data-testid="admin-coupons-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-ink-950 tracking-tight">Coupon Codes</h1>
          <p className="text-ink-500 mt-1 text-sm">
            Validated at checkout. Discount is delivered as bonus tokens on subscribe.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => { setShowBulkForm((s) => !s); if (showForm) setShowForm(false); }}
            variant="outline"
            className="gap-2 rounded-full"
            data-testid="new-batch-btn"
          >
            <Package className="w-4 h-4" />{showBulkForm ? "Close" : "Bulk batch"}
          </Button>
          <Button
            onClick={() => { setShowForm((s) => !s); if (showBulkForm) setShowBulkForm(false); }}
            className="gap-2 bg-ink-950 hover:bg-ink-800 text-white rounded-full"
            data-testid="new-coupon-btn"
          >
            <Plus className="w-4 h-4" />{showForm ? "Close" : "New coupon"}
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="p-6 rounded-2xl bg-white border border-ink-200 space-y-4" data-testid="new-coupon-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-ink-700">Code</label>
              <Input
                placeholder="SUMMER25"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                data-testid="coupon-code-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Description</label>
              <Input
                placeholder="Summer promo"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Discount % (0-100)</label>
              <Input
                type="number" min="0" max="100"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                data-testid="coupon-discount-input"
              />
              <p className="text-xs text-ink-400 mt-1">
                Auto-converted to bonus tokens at redemption.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Flat bonus tokens (override %)</label>
              <Input
                type="number" min="0"
                value={form.bonus_tokens}
                onChange={(e) => setForm({ ...form, bonus_tokens: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Max uses (blank = unlimited)</label>
              <Input
                type="number" min="1"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Applies to tiers</label>
              <div className="flex gap-2 mt-2">
                {["premium", "platinum"].map((t) => {
                  const active = form.applicable_tiers.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        const next = active
                          ? form.applicable_tiers.filter((x) => x !== t)
                          : [...form.applicable_tiers, t];
                        setForm({ ...form, applicable_tiers: next });
                      }}
                      className={`px-3 py-1 text-sm rounded-full border ${
                        active
                          ? "bg-ink-950 text-white border-ink-950"
                          : "bg-white text-ink-700 border-ink-300"
                      }`}
                      data-testid={`coupon-tier-${t}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={form.single_use_per_user}
                onChange={(e) => setForm({ ...form, single_use_per_user: e.target.checked })}
              /> Single-use per user
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              /> Active
            </label>
          </div>

          <Button
            onClick={handleCreate}
            disabled={creating}
            className="bg-coral-500 hover:bg-coral-600 text-white rounded-full"
            data-testid="create-coupon-btn"
          >
            {creating ? "Creating…" : "Create coupon"}
          </Button>
        </div>
      )}

      {/* Bulk batch form */}
      {showBulkForm && (
        <div className="p-6 rounded-2xl bg-white border border-ink-200 space-y-4" data-testid="new-batch-form">
          <div>
            <h3 className="font-bold text-ink-950 text-base">Bulk generate codes</h3>
            <p className="text-xs text-ink-500 mt-1">
              For influencer drops &amp; partner campaigns. Each code is single-use, codes expire after the chosen window.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-ink-700">Prefix</label>
              <Input
                placeholder="MIA"
                value={bulkForm.prefix}
                onChange={(e) => setBulkForm({ ...bulkForm, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                maxLength={12}
                data-testid="batch-prefix-input"
              />
              <p className="text-xs text-ink-400 mt-1">Codes will look like <code>{bulkForm.prefix || "MIA"}-X4F2</code></p>
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Batch label</label>
              <Input
                placeholder="Mia drop — Mar 2026"
                value={bulkForm.batch_label}
                onChange={(e) => setBulkForm({ ...bulkForm, batch_label: e.target.value })}
                data-testid="batch-label-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Number of codes (1 - 2000)</label>
              <Input
                type="number" min="1" max="2000"
                value={bulkForm.count}
                onChange={(e) => setBulkForm({ ...bulkForm, count: e.target.value })}
                data-testid="batch-count-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Valid for (days)</label>
              <Input
                type="number" min="1" max="365"
                value={bulkForm.valid_days}
                onChange={(e) => setBulkForm({ ...bulkForm, valid_days: e.target.value })}
                data-testid="batch-valid-days-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Discount %</label>
              <Input
                type="number" min="0" max="100"
                value={bulkForm.discount_percent}
                onChange={(e) => setBulkForm({ ...bulkForm, discount_percent: e.target.value })}
                data-testid="batch-discount-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink-700">Bonus tokens (override %)</label>
              <Input
                type="number" min="0"
                value={bulkForm.bonus_tokens}
                onChange={(e) => setBulkForm({ ...bulkForm, bonus_tokens: e.target.value })}
              />
            </div>
          </div>
          <Button
            onClick={handleCreateBatch}
            disabled={creatingBulk}
            className="bg-coral-500 hover:bg-coral-600 text-white rounded-full gap-2"
            data-testid="create-batch-btn"
          >
            <Package className="w-4 h-4" />
            {creatingBulk ? "Generating…" : `Generate ${bulkForm.count || 0} codes`}
          </Button>
        </div>
      )}

      {/* Last-batch quick view */}
      {lastBatchCodes && (
        <div
          className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 space-y-3"
          data-testid="last-batch-codes"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-bold text-emerald-800">
              ✅ {lastBatchCodes.codes.length} codes ready
            </p>
            <div className="flex gap-2">
              <Button
                size="sm" variant="outline"
                onClick={() => copyCodesToClipboard(lastBatchCodes.codes)}
                className="gap-1"
              >
                <Copy className="w-3.5 h-3.5" /> Copy all
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => handleExportBatch(lastBatchCodes.batchId, "latest")}
                className="gap-1"
                data-testid="export-last-batch-btn"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </Button>
              <button
                onClick={() => setLastBatchCodes(null)}
                className="text-emerald-700 hover:text-emerald-900"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="max-h-32 overflow-y-auto bg-white rounded-lg p-2 font-mono text-xs text-ink-700 grid grid-cols-2 sm:grid-cols-4 gap-1">
            {lastBatchCodes.codes.slice(0, 24).map((c) => (
              <div key={c} className="truncate">{c}</div>
            ))}
            {lastBatchCodes.codes.length > 24 && (
              <div className="text-ink-500 italic col-span-full">
                …and {lastBatchCodes.codes.length - 24} more (export CSV to see all)
              </div>
            )}
          </div>
        </div>
      )}

      {/* Batches list */}
      {batches.length > 0 && (
        <div className="space-y-2" data-testid="batches-section">
          <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-ink-500">Bulk Batches</h2>
          <div className="grid gap-2">
            {batches.map((b) => (
              <div
                key={b.id}
                className="p-4 rounded-xl bg-white border border-ink-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                data-testid={`batch-row-${b.id}`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-coral-500" />
                    <span className="font-bold text-ink-950">{b.batch_label}</span>
                    <Badge className="bg-ink-100 text-ink-500 border-ink-200">{b.prefix}-XXXX</Badge>
                  </div>
                  <p className="text-xs text-ink-500 mt-1">
                    <strong className="text-ink-950">{b.redeemed_count}</strong>/{b.count} redeemed
                    ({b.redemption_pct}%) · {b.discount_percent}% off
                    {b.bonus_tokens > 0 && <> · +{b.bonus_tokens} tokens</>}
                    {b.valid_until && (
                      <> · expires {new Date(b.valid_until).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => handleExportBatch(b.id, b.batch_label)}
                  className="gap-1 rounded-full"
                  data-testid={`batch-export-${b.id}`}
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-3">
        {coupons.length === 0 && (
          <p className="text-center text-ink-500 py-8">No coupons yet. Create one above.</p>
        )}
        {coupons.map((c) => (
          <div
            key={c.code}
            className="p-5 rounded-2xl bg-white border border-ink-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            data-testid={`coupon-row-${c.code}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-coral-500/10 text-coral-500 flex items-center justify-center">
                <Tag className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-ink-950">{c.code}</span>
                  {c.is_active ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                  ) : (
                    <Badge className="bg-ink-100 text-ink-500 border-ink-200">Inactive</Badge>
                  )}
                </div>
                <p className="text-sm text-ink-500 mt-1">
                  {c.discount_percent > 0 && <span>{c.discount_percent}% off · </span>}
                  {c.bonus_tokens > 0 && <span>+{c.bonus_tokens} tokens · </span>}
                  Used {c.used_count || 0}{c.max_uses ? `/${c.max_uses}` : ""} ·
                  {(c.applicable_tiers || []).length ? ` ${c.applicable_tiers.join(", ")}` : " any paid"}
                </p>
                {c.description && (
                  <p className="text-xs text-ink-400 mt-1">{c.description}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm" variant="outline"
                onClick={() => viewRedemptions(c.code)}
                data-testid={`coupon-redemptions-${c.code}`}
              >
                <Users className="w-4 h-4 mr-1" /> Uses
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => toggleActive(c)}
                data-testid={`coupon-toggle-${c.code}`}
              >
                <Power className="w-4 h-4 mr-1" />
                {c.is_active ? "Disable" : "Enable"}
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => handleDelete(c.code)}
                className="text-red-600 hover:bg-red-50 border-red-200"
                data-testid={`coupon-delete-${c.code}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {redemptionsOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRedemptionsOpen(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-ink-950">Redemptions — {redemptionsOpen}</h3>
              <button onClick={() => setRedemptionsOpen(null)} className="text-ink-500 hover:text-ink-950">
                <X className="w-5 h-5" />
              </button>
            </div>
            {redemptions.length === 0 ? (
              <p className="text-ink-500 text-sm">No redemptions yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-ink-500 border-b border-ink-200">
                  <tr>
                    <th className="text-left py-2">User</th>
                    <th className="text-left py-2">Tier</th>
                    <th className="text-left py-2">+Tokens</th>
                    <th className="text-left py-2">When</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r) => (
                    <tr key={r.id} className="border-b border-ink-100">
                      <td className="py-2 text-ink-700">{r.user_email || r.user_id}</td>
                      <td className="py-2 capitalize">{r.tier}</td>
                      <td className="py-2 text-coral-500 font-medium">+{r.bonus_tokens_granted}</td>
                      <td className="py-2 text-ink-500">{new Date(r.redeemed_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCouponsPage;
